import React, { cloneElement, useEffect, useState } from 'react'

import {
  useInteractions,
  useHover,
  useFocus,
  useRole,
  useFloating,
  autoUpdate,
} from '@floating-ui/react-dom-interactions'

import { TooltipProps } from '@customTypes'
import { noop } from '_helpers/functions'

import { Body } from './components/Body'

export const Tooltip: React.FC<TooltipProps> = ({
  label,
  placement,
  children,
  shouldShow,
  className,
}) => {
  const [open, setOpen] = useState(false)

  const { x, y, reference, floating, strategy, context, refs, update } =
    useFloating({
      placement,
      open,
      onOpenChange: setOpen,
    })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context),
    useFocus(context),
    useRole(context, { role: 'tooltip' }),
  ])

  useEffect(() => {
    if (refs.reference.current && refs.floating.current && open) {
      return autoUpdate(refs.reference.current, refs.floating.current, update)
    }
    return noop
  }, [refs.reference, refs.floating, update, open])

  const childWithRef = cloneElement(
    children,
    getReferenceProps({
      ref: reference,
      ...children.props,
    })
  )

  const tooltipProps = {
    ...getFloatingProps({
      ref: floating,
      className: 'Tooltip',
      style: {
        position: strategy,
      },
    }),
    placement,
    x,
    y,
  }

  return (
    <>
      {childWithRef}
      {shouldShow && open && (
        <Body className={className} {...tooltipProps}>
          {label}
        </Body>
      )}
    </>
  )
}

Tooltip.defaultProps = {
  placement: 'top',
}
